<template>
	<div>
		<!-- dialog -->
		<v-dialog v-model="dialogData.show" max-width="300" :persistent="submitLoading">
			<v-form ref="userForm" @submit.prevent="submitUser" :disabled="submitLoading">
				<v-card>
					<v-card-title class="justify-center text-capitalize primary--text">
						<v-btn @click="dialogData.show = false" icon>
							<v-icon>$close</v-icon>
						</v-btn>
						<v-spacer/>
						<span class="me-8">
							{{!userId ? $T('add-user') : $T('edit-user')}}
						</span>
						<v-spacer/>
					</v-card-title>
					<v-card-text class="pb-0">
						<v-row dense>
							<!-- username -->
							<v-col cols="12">
								<v-text-field
									v-model="dialogData.username"
									:label="$T('inputs.username')"
									outlined
									dense
									hide-details
									:rules="rules.required"
								/>
							</v-col>
							
							<!-- password -->
							<v-col cols="12">
								<v-text-field
									v-model="dialogData.password"
									:label="$T('inputs.password')"
									:type="dialogData.showPassword ? 'text' : 'password'"
									outlined
									dense
									hide-details
									:append-icon="dialogData.showPassword ? 'mdi-eye-off' : 'mdi-eye'"
									@click:append="dialogData.showPassword = !dialogData.showPassword"
									:rules="!userId ? rules.password : []"
								/>
							</v-col>
							
							<!-- confirm-password -->
							<v-col cols="12">
								<v-text-field
									v-model="dialogData.confirmPassword"
									:label="$T('inputs.confirm-password')"
									:type="dialogData.showPassword ? 'text' : 'password'"
									outlined
									dense
									hide-details
									:append-icon="dialogData.showPassword ? 'mdi-eye-off' : 'mdi-eye'"
									@click:append="dialogData.showPassword = !dialogData.showPassword"
									:rules="[val => dialogData.password === val]"
								/>
							</v-col>

							<!-- password note -->
							<v-col v-if="userId" cols="12" class="text-caption">
								{{$T('password-note')}}
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn type="submit" color="primary" :loading="submitLoading">
							{{$T('save')}}
						</v-btn>
						<v-btn @click="dialogData.show = false" :disabled="submitLoading">
							{{$T('close')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- delete dialog -->
		<v-delete-dialog
			v-model="deleteDialog"
			:module-type="userId ? 'users' : 'roles'"
			:ids="userId ? userId : $route.params.id"
			@on-delete="userId ? fetchRoleUsers() : (() => {
				$router.replace({ name: 'roles-management' });
				$emit('update-roles');
			})()"
			@input="!$event ? userId = null : null"
		>
			{{$T('are-you-sure-you-want-to-$', { message: userId ? $T('the-user') : $T('the-role') })}}
			<p v-if="!userId" class="mb-0">{{$T('deleting-role-alert')}}</p>
		</v-delete-dialog>
		
		<v-form
			ref="form"
			@submit.prevent="submit"
			:disabled="Boolean(disableFieldsToPreview) || submitLoading"
		>
			<!-- role name & actions-->
			<v-row dense align="center" class="mb-1">
				<!-- role name -->
				<v-col lg="3" md="4" sm="6" cols="12">
					<v-text-field
						v-model="roleName"
						:label="$T('inputs.role-name')"
						dense
						outlined
						hide-details
						:rules="rules.required"
					/>
				</v-col>

				<v-spacer/>

				<!-- actions -->
				<v-col v-if="selfPermissions.users.isReadWrite" cols="auto">
					<template v-if="$route.query.preview">
						<v-btn
							v-if="selfPermissions.managingSettings.isReadWrite || isAdmin"
							height="32"
							class="me-2"
							color="warning-color white--text"
							key="edit"
							replace
							:to="{ name: 'role', params: { id: $route.params.id } }"
						>
							{{$T('edit')}}
						</v-btn>
						<v-btn
							v-if="selfPermissions.managingSettings.isReadWrite || isAdmin"
							height="32"
							color="alert-color white--text"
							key="delete"
							@click="deleteDialog = true"
						>
							{{$T('delete')}}
						</v-btn>
					</template>
					<template v-else>
						<v-btn
							height="32"
							class="me-2"
							color="primary"
							key="save"
							type="submit"
							:loading="submitLoading"
						>
							{{$T('save')}}
						</v-btn>
						<v-btn
							height="32"
							key="cancel"
							replace
							:to="{
								name: $route.params.id ? 'role' : 'roles-management',
								params: { id: $route.params.id },
								query: { preview: 1 }
							}"
							:disabled="submitLoading"
						>
							{{$T('close')}}
						</v-btn>
					</template>
				</v-col>
			</v-row>

			<!-- permissions -->
			<v-simple-table
				dense
				fixed-header
				:height="386"
				class="elevation-2"
			>
				<thead>
					<tr>
						<th>{{$T('permission-type')}}</th>
						<th>{{$T('read-and-write')}}</th>
						<th>{{$T('read-only')}}</th>
						<th>{{$T('no-permission')}}</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="permission in permissions" :key="permission.id">
						<td style="width: 200px">{{ permission.name }}</td>

						<!-- read-and-write -->
						<td style="width: 120px">
							<v-checkbox
								v-model="permission.value"
								:ripple="false"
								:true-value="actionType.readAndWrite"
								:readonly="permission.value === 2"
								:false-value="null"
								class="ma-0"
								hide-details
								on-icon="mdi-radiobox-marked"
								off-icon="mdi-radiobox-blank"
							/>
						</td>
						
						<!-- read-only -->
						<td style="width: 120px">
							<v-checkbox
								v-model="permission.value"
								:ripple="false"
								:true-value="actionType.read"
								:readonly="permission.value === 1"
								:false-value="null"
								class="ma-0"
								hide-details
								on-icon="mdi-radiobox-marked"
								off-icon="mdi-radiobox-blank"
							/>
						</td>
						
						<!-- no-permission -->
						<td style="width: 120px">
							<v-checkbox
								v-model="permission.value"
								:ripple="false"
								:true-value="actionType.none"
								:readonly="permission.value === 3"
								:false-value="null"
								class="ma-0"
								hide-details
								on-icon="mdi-radiobox-marked"
								off-icon="mdi-radiobox-blank"
							/>
						</td>

						<!-- info -->
						<td class="text-end" style="width: 40px">
							<v-tooltip top color="grey darken-2" open-delay="500">
								<template v-slot:activator="{ attr, on }">
									<v-icon v-on="on" v-bind="attr">mdi-information-outline</v-icon>
								</template>
								{{permission.info}}
							</v-tooltip>
						</td>
					</tr>
				</tbody>
			</v-simple-table>

			<!-- users -->
			<template v-if="$route.params.id">
				<div class="d-flex justify-space-between align-center">
					<span class="text-h6 mt-2 mb-1">{{$T('Users')}}</span>

					<!-- add -->
					<v-btn
						v-if="selfPermissions.users.isReadWrite"
						color="primary"
						height="30"
						@click="dialogData.show = true;"
					>
						<v-icon class="me-1">$add</v-icon>
						{{$T('add')}}
					</v-btn>
				</div>
				<bee-handy-table
					:headers="[
						{ text: $T('headers.user'), name: 'username', value: 'username' },
						{ text: '', name: 'actions', value: 'actions', width: 100 },
					]"
					:items="loading ? [] : users"
					:height="$vuetify.breakpoint.height - 544"
					:items-per-page="-1"
					:data-table-props="{ disableSort: true }"
					:loading="loading"
					dense
					hide-default-footer
				>
					<!-- actions -->
					<template v-slot:item.actions="{ item }">
						<div
							v-if="item.username !== 'admin' && selfPermissions.users.isReadWrite"
							class="d-flex"
						>
							<v-btn
								width="34"
								height="34"
								icon
								color="warning-color"
								class="ms-1"
								@click="userId = item.id; dialogData.show = true;"
							>
								<v-icon>$edit</v-icon>
							</v-btn>
							
							<v-btn
								width="34"
								height="34"
								icon
								color="alert-color"
								class="ms-1"
								@click="userId = item.id; deleteDialog = true;"
							>
								<v-icon>$delete</v-icon>
							</v-btn>
						</div>
					</template>
				</bee-handy-table>
			</template>
		</v-form>
	</div>
</template>

<script>
import rules from '@/helpers/validation-rules'
import { permission, actionType } from '@/helpers/enums'
import { mapGetters, mapState } from 'vuex'

export default {
	name: 'Role',

	data() {
		return {
			loading: false,
			submitLoading: false,

			deleteDialog: false,
			
			dialog: false,
			users: [],
			
			rules,
			actionType,

			// data
			dialogData: {
				show: false,
				username: null,
				password: null,
				confirmPassword: null,
				showPassword: false,
			},
			userId: null,

			roleName: null,

			permissions: [
				{
					id: permission.managingPurchases,
					name: this.$T('permissions.managing-purchase'),
					info: this.$T('permissions-description.managing-purchase'),
					value: false
				},
				{
					id: permission.managingSimulations,
					name: this.$T('permissions.managing-purchase-simulation'),
					info: this.$T('permissions-description.managing-purchase-simulation'),
					value: false
				},
				{
					id: permission.managingReceives,
					name: this.$T('permissions.managing-receives'),
					info: this.$T('permissions-description.managing-receives'),
					value: false
				},
				{
					id: permission.sales,
					name: this.$T('permissions.sales'),
					info: this.$T('permissions-description.sales'),
					value: false
				},
				{
					id: permission.managingInventory,
					name: this.$T('permissions.managing-inventory'),
					info: this.$T('permissions-description.managing-inventory'),
					value: false
				},
				{
					id: permission.managingItems,
					name: this.$T('permissions.managing-items'),
					info: this.$T('permissions-description.managing-items'),
					value: false
				},
				{
					id: permission.managingAccounts,
					name: this.$T('permissions.managing-accounts'),
					info: this.$T('permissions-description.managing-accounts'),
					value: false
				},
				{
					id: permission.managingUsers,
					name: this.$T('permissions.managing-users'),
					info: this.$T('permissions-description.managing-users'),
					value: false
				},
				{
					id: permission.viewStatistics,
					name: this.$T('permissions.statistics'),
					info: this.$T('permissions-description.statistics'),
					value: false
				},
				{
					id: permission.fiscalYears,
					name: this.$T('permissions.fiscal-years'),
					info: this.$T('permissions-description.fiscal-years'),
					value: false
				},
				{
					id: permission.materialJournal,
					name: this.$T('permissions.material-journal'),
					info: this.$T('permissions-description.fiscal-years'),
					value: false
				},
				{
					id: permission.managingSettings,
					name: this.$T('permissions.managing-settings'),
					info: this.$T('permissions-description.managing-settings'),
					value: false
				},
				{
					id: permission.managingProductionOrders,
					name: this.$T('permissions.managing-production-orders'),
					info: this.$T('permissions-description.managing-production-orders'),
					value: false
				},
				{
					id: permission.managingOrderResults,
					name: this.$T('permissions.managing-order-results'),
					info: this.$T('permissions-description.managing-order-results'),
					value: false
				},
				{
					id: permission.managingItemsStatement,
					name: this.$T('permissions.managing-items-statement'),
					info: this.$T('permissions-description.managing-items-statement'),
					value: false
				},
				{
					id: permission.managingExperimentalSimulation,
					name: this.$T('permissions.managing-experimental-simulation'),
					info: this.$T('permissions-description.managing-experimental-simulation'),
					value: false
				},
				{
					id: permission.managingMaterialSimulation,
					name: this.$T('permissions.managing-material-simulation'),
					info: this.$T('permissions-description.managing-material-simulation'),
					value: false
				},
				{
					id: permission.managingReceivingJournals,
					name: this.$T('permissions.managing-receiving-journals'),
					info: this.$T('permissions-description.managing-receiving-journals'),
					value: false
				},
				{
					id: permission.managingDeliveryJournals,
					name: this.$T('permissions.delivery-journals'),
					info: this.$T('permissions-description.delivery-journals'),
					value: false
				}
			],
		}
	},

	watch: {
		$route: {
			handler(newVal, oldVal) {
				if (newVal.params.id) {
					if (!oldVal || newVal.params.id !== oldVal.params.id) {
						this.fetchRoleUsers();
					}

					const role = this.getRoleById(newVal.params.id);
					this.roleName = role.name;

					for (let i = 0; i < this.permissions.length; i++) {
						this.permissions[i].value =
							role.permissions
								.find(c => c.permissionsNames === this.permissions[i].id)
								?.rolePermissions ?? actionType.none;
					}

					if (newVal.query.delete) this.deleteDialog = true;
				} else if (this.$route.name === 'role') {
					this.$refs.form.reset();
				}
			},
			immediate: true
		},

		'dialogData.show'(val) {
			if (val) {
				if (this.userId) {
					const user = this.users.find(c => c.id === this.userId);
					this.dialogData.username = user.username;
				}
			} else {
				this.$refs.userForm.reset();
				this.dialogData.showPassword = false;
				this.userId = null;
			}
		}
	},

	computed: {
		...mapState({
			selfPermissions: state => state.auth.self.permissions,
			isAdmin: state => state.auth.self.isAdmin
		}),
		...mapGetters({
			getRoleById: 'roles/getRoleById',
		}),

		disableFieldsToPreview() {
			return this.$route.query.preview;
		}
	},

	methods: {
		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				const actionName = !this.$route.params.id ? 'create' : 'update';
				this.$store.dispatch(`roles/${actionName}`, {
					id: this.$route.params.id,
					name: this.roleName,
					parentId: null,
					rolePermissions: this.permissions.map(c => ({
						permissionsNames: c.id,
						rolePermissions: c.value || actionType.none
					}))
				}).then((id) => {
					this.$emit('update-roles');
					this.$router.replace({
						name: 'role',
						params: { id: this.$route.params.id || id },
						query: { preview: 1 },
					})
				}).finally(() => {
					this.submitLoading = true;
				})
			}
		},

		submitUser() {
			if (this.$refs.userForm.validate()) {
				this.submitLoading = true;
				const actionName = !this.userId ? 'create' : 'update';
				this.$store.dispatch(`users/${actionName}`, {
					id: this.userId,
					userName: this.dialogData.username,
					password: this.dialogData.password,
					roleId: this.$route.params.id
				}).then(() => {
					this.dialogData.show = false;
					this.fetchRoleUsers();
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},

		fetchRoleUsers() {
			this.loading = true;
			this.$emit('change-disable-state', true);

			this.$store.dispatch('roles/fetchRoleUsers', {
				id: this.$route.params.id
			}).then(data => {
				this.users = data;
			}).finally(() => {
				this.loading = false;
				this.$emit('change-disable-state', false);
			})
		}
	}
}
</script>

<style>

</style>